import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  getUserProfile, 
  getCurrentProgram, 
  setupMultiSportProgram,
  clearAllScheduledActivities,
  getPersonasConfig,
  fetchProgramTemplate
} from '../utils/database';
import { useTheme } from '../contexts/ThemeContext';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import '../styles/Dashboard.css';
import '../styles/Program.css';

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_LABELS_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const ACTIVITY_DISPLAY_NAMES = {
  strength: 'Strength Workout',
  strength_workout: 'Strength Workout',
  practice: 'Practice',
  game: 'Game',
  rest: 'Rest Day',
  drills: 'Drills',
  endurance: 'Cardio',
  cardio: 'Cardio',
  recovery: 'Recovery',
  plyometric: 'Plyometrics',
  plyometrics: 'Plyometrics',
  skill: 'Skill Work',
  activation: 'Activation',
  tournament: 'Tournament',
  race: 'Race',
  swim: 'Swim',
  bike: 'Bike',
  run: 'Run',
  walk: 'Walk',
  yoga: 'Yoga',
  mobility: 'Mobility',
};

function getActivityDisplayName(raw) {
  if (!raw) return 'Rest Day';
  const key = Array.isArray(raw) ? raw[0] : (typeof raw === 'object' ? (raw.activityType || raw.name || '') : raw);
  const str = String(key).toLowerCase().trim();
  return ACTIVITY_DISPLAY_NAMES[str] || str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

function getActivityTypeKey(raw) {
  if (!raw) return 'rest';
  const key = Array.isArray(raw) ? raw[0] : (typeof raw === 'object' ? (raw.activityType || '') : raw);
  return String(key).toLowerCase().trim();
}

function capitalize(val) {
  if (!val) return '';
  const str = String(val);
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

function formatDateRange(startDate, endDate) {
  if (!startDate && !endDate) return '';
  const opts = { month: 'short', day: 'numeric' };
  const parts = [];
  if (startDate) parts.push(new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', opts));
  if (endDate) parts.push(new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', opts));
  return parts.join(' – ');
}

function getMacroId(macro) {
  return macro?.macrocycle_id || macro?.macrocycleId || macro?.id;
}

function getMesoId(meso) {
  return meso?.mesocycle_id || meso?.mesocycleId || meso?.id;
}

function Program() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [, setLoading] = useState(true);
  const [currentProgram, setCurrentProgram] = useState(null);
  const [programTemplate, setProgramTemplate] = useState(null);
  const [programLoaded, setProgramLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');
  const [personasData, setPersonasData] = useState({ personas: [], experienceLevels: [] });
  
  const [selectedMacroId, setSelectedMacroId] = useState(null);
  const [hoveredPhase, setHoveredPhase] = useState(null);
  const [hoveredWeek, setHoveredWeek] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }

        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }

        setUser(userData);
        setUserLoaded(true);
        
        try {
          const programData = await getCurrentProgram(userId);
          setCurrentProgram(programData);

          // Fetch program template for enrichment (focus, weekly_activity_mix, goals, etc.)
          const progId = programData?.program?.id;
          if (progId) {
            const tmpl = await fetchProgramTemplate(progId);
            setProgramTemplate(tmpl);
          }
        } catch (error) {
          console.error('Error loading current program:', error);
          setCurrentProgram(null);
        } finally {
          setProgramLoaded(true);
        }

        setLoading(false);
      } catch (error) {
        console.error('[Program] Error loading user:', error);
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [userId, navigate]);

  useEffect(() => {
    getPersonasConfig().then((data) => {
      if (data?.personas) setPersonasData(data);
    }).catch(() => {});
  }, []);

  // --- Derived data from DB ---

  const program = currentProgram?.program;
  const activeMesocycle = currentProgram?.mesocycle;
  const currentWeek = currentProgram?.currentWeek || 1;

  // Filter macrocycles/mesocycles to only those belonging to the current program template.
  // The user's macrocycles subcollection may contain docs from prior program enrollments.
  const templateMacroIds = new Set(
    (programTemplate?.macrocycles || []).map(m => getMacroId(m)).filter(Boolean)
  );

  const allMacrocycles = (currentProgram?.allMacrocycles || [])
    .filter(m => templateMacroIds.size === 0 || templateMacroIds.has(getMacroId(m)))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const allMesocycles = (currentProgram?.allMesocycles || [])
    .filter(m => {
      if (templateMacroIds.size === 0) return true;
      return templateMacroIds.has(m.macrocycleId);
    });

  // Re-resolve the active macrocycle from the filtered set (the one for today's date)
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const activeMacrocycle = allMacrocycles.find(m =>
    m.startDate && m.endDate && todayStr >= m.startDate && todayStr <= m.endDate
  ) || currentProgram?.macrocycle;

  const activeMacroId = getMacroId(activeMacrocycle);
  const activeMesoId = getMesoId(activeMesocycle);

  // The user-selected macrocycle (defaults to the current one)
  const effectiveSelectedId = selectedMacroId || activeMacroId;
  const selectedMacrocycle = allMacrocycles.find(m => getMacroId(m) === effectiveSelectedId) || activeMacrocycle;
  const selectedMacroIdResolved = getMacroId(selectedMacrocycle);
  const isViewingCurrent = selectedMacroIdResolved === activeMacroId;

  // Enrich macrocycle with template data (for backwards compat when focus/weekly_activity_mix not stored)
  const enrichMacro = (macro) => {
    if (!macro) return macro;
    const macroId = getMacroId(macro);
    const tmplMacros = programTemplate?.macrocycles || [];
    const tmplMatch = tmplMacros.find(m => getMacroId(m) === macroId);
    return {
      ...macro,
      focus: macro.focus || tmplMatch?.focus || null,
      weekly_activity_mix: macro.weekly_activity_mix || tmplMatch?.weekly_activity_mix || null,
    };
  };

  const enrichedSelectedMacro = enrichMacro(selectedMacrocycle);

  // Mesocycles belonging to the selected macrocycle
  const mesosForSelectedMacro = allMesocycles
    .filter(m => m.macrocycleId === selectedMacroIdResolved)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // For week display: use active mesocycle's totalWeeks if viewing current, otherwise first meso
  const selectedMesoTotalWeeks = (isViewingCurrent && activeMesocycle?.totalWeeks)
    || mesosForSelectedMacro[0]?.totalWeeks || 4;

  // Weekly schedule from selected macrocycle's weekly_activity_mix
  const getWeeklySchedule = (enrichedMacro) => {
    const mix = enrichedMacro?.weekly_activity_mix;
    if (!mix?.day_assignments || typeof mix.day_assignments !== 'object') return null;
    return DAY_ORDER.map((day, idx) => {
      const raw = mix.day_assignments[day];
      return {
        dayKey: day,
        dayLabel: DAY_LABELS[idx],
        dayShort: DAY_LABELS_SHORT[idx],
        activity: getActivityDisplayName(raw),
        type: getActivityTypeKey(raw),
      };
    });
  };

  // Program description: prefer program doc, fall back to template
  const programDescription = program?.description || programTemplate?.description || null;
  const programGoals = program?.goals?.length ? program.goals : (programTemplate?.goals || []);
  const programName = program?.name || programTemplate?.name || 'Your Program';

  // Persona / program display info
  const getPersonaInfo = () => {
    if (!user?.personaId) return null;
    return personasData.personas.find(p => p.id === user.personaId) || null;
  };

  const getSelectedProgramInfo = () => {
    if (!user?.selectedProgramId || !user?.personaId) return null;
    const persona = personasData.personas.find(p => p.id === user.personaId);
    return persona?.programs?.find(prog => prog.id === user.selectedProgramId) || null;
  };

  const getExperienceLevelInfo = () => {
    if (!user?.experienceLevel) return null;
    return personasData.experienceLevels?.find(l => l.id === user.experienceLevel) || null;
  };

  const hasProgramData = allMacrocycles.length > 0 || !!program;

  // Handle import workouts
  const handleImportWorkouts = async (programType) => {
    setImporting(true);
    setImportError('');
    setImportSuccess('');

    try {
      await clearAllScheduledActivities(userId);

      let result;
      if (programType === 'multi_sport_auto') {
        const month = new Date().getMonth();
        let season;
        if (month >= 11 || month <= 1) season = 'winter';
        else if (month >= 2 && month <= 4) season = 'spring';
        else if (month >= 5 && month <= 7) season = 'summer';
        else season = 'fall';
        result = await setupMultiSportProgram(userId, season, 12);
      }

      if (result?.success) {
        const weekInfo = result.currentWeekInSeason ? ` (starting at week ${result.currentWeekInSeason})` : '';
        setImportSuccess(`Successfully imported ${result.activitiesCreated} activities${weekInfo}!`);
        const programData = await getCurrentProgram(userId);
        setCurrentProgram(programData);
        if (programData?.program?.id) {
          const tmpl = await fetchProgramTemplate(programData.program.id);
          setProgramTemplate(tmpl);
        }
        setTimeout(() => {
          setShowImportModal(false);
          setImportSuccess('');
        }, 2000);
      }
    } catch (error) {
      console.error('[Program] Error importing workouts:', error);
      setImportError('Failed to import workouts. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const persona = getPersonaInfo();
  const experienceLevel = getExperienceLevelInfo();
  const selectedProgram = getSelectedProgramInfo();
  const weeklySchedule = getWeeklySchedule(enrichedSelectedMacro);

  const handleMacroSelect = (macroId) => {
    setSelectedMacroId(macroId);
    setHoveredPhase(null);
    setHoveredWeek(null);
  };

  const todayDayIndex = today.getDay(); // 0=Sun
  const todayDayKey = DAY_ORDER[(todayDayIndex + 6) % 7];

  return (
    <>
    <div className="dashboard-page has-bottom-nav">
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />

      <DashboardSidebar userId={userId} user={user} />

      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            <h1>Program</h1>
            <p style={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)', marginTop: '8px' }}>
              {!userLoaded ? (
                <span className="program-loading-text">Loading program...</span>
              ) : (
                <>
                  {persona?.name || 'No persona selected'} • {selectedProgram?.name || experienceLevel?.name || programName}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="dashboard-content">

          {/* Loading skeleton */}
          {!userLoaded && (
            <div className="dashboard-card settings-section program-loading-skeleton">
              <div className="program-skeleton-header">
                <div className="program-skeleton-line wide" />
                <div className="program-skeleton-line medium" />
              </div>
              <div className="program-skeleton-content">
                <div className="program-skeleton-block" />
                <div className="program-skeleton-block short" />
              </div>
            </div>
          )}

          {/* No persona selected */}
          {userLoaded && !user?.personaId && !program && (
            <div className="dashboard-card settings-section">
              <h2 className="settings-section-title">No Training Persona Selected</h2>
              <p className="settings-section-description">
                Please select a training persona in Settings to see your program details.
              </p>
              <button
                onClick={() => navigate(`/${userId}/settings`)}
                style={{
                  background: '#FF0000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif',
                  cursor: 'pointer'
                }}
              >
                Go to Settings
              </button>
            </div>
          )}

          {/* No program data yet — persona selected but no macrocycles in DB */}
          {userLoaded && user?.personaId && !hasProgramData && programLoaded && (
            <div className="dashboard-card settings-section">
              <div style={{ textAlign: 'left', padding: '40px 20px' }}>
                <h2 style={{ color: isDark ? '#FFF' : '#1A1A1A', fontSize: '24px', marginBottom: '12px' }}>
                  {persona?.name || 'Your'} Program
                </h2>
                <h3 style={{ color: '#FF0000', fontSize: '20px', marginBottom: '16px' }}>
                  Not Set Up Yet
                </h3>
                <p style={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', maxWidth: '500px', lineHeight: '1.6' }}>
                  Your program hasn't been imported yet. Go to your calendar or use the setup flow to get started with your training plan.
                </p>
              </div>
            </div>
          )}

          {/* Full Program Details — driven by DB data */}
          {userLoaded && hasProgramData && (
            <>
              {/* Import Workouts Card — only if no program doc exists */}
              {programLoaded && !program && (
                <div className="dashboard-card settings-section">
                  <h2 className="settings-section-title">Get Started</h2>
                  <p className="settings-section-description">
                    Import your workout program to populate your calendar with scheduled workouts based on your training persona.
                  </p>
                  <button
                    onClick={() => setShowImportModal(true)}
                    style={{
                      background: '#FF0000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '16px 32px',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: 'Poppins, sans-serif',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#d40000'}
                    onMouseLeave={(e) => e.target.style.background = '#FF0000'}
                  >
                    <span style={{ fontSize: '20px' }}>+</span>
                    Import Workouts
                  </button>
                </div>
              )}

              {/* Periodization Overview Card */}
              <div className="dashboard-card settings-section">
                <h2 className="settings-section-title">Periodization Overview</h2>
                {programDescription && (
                  <p className="settings-section-description">{programDescription}</p>
                )}

                {/* Macrocycle Selector */}
                {allMacrocycles.length > 1 && (
                  <div className="program-macro-selector">
                    {allMacrocycles.map((macro, idx) => {
                      const macroId = getMacroId(macro);
                      const isSelected = macroId === selectedMacroIdResolved;
                      const isCurrent = macroId === activeMacroId;
                      return (
                        <button
                          key={macroId || idx}
                          className={`program-macro-selector-btn ${isSelected ? 'selected' : ''} ${isCurrent ? 'is-current' : ''}`}
                          onClick={() => handleMacroSelect(macroId)}
                        >
                          <span className="program-macro-selector-name">{macro.name}</span>
                          {isCurrent && <span className="program-macro-selector-badge">Now</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Nested Periodization Visualization */}
                {selectedMacrocycle && (
                <div style={{ marginBottom: '24px' }}>
                  <div className="program-macro-box">
                    <div className="program-level-header">
                      <div className="program-level-header-left">
                        <div className="program-level-badge macro">Macrocycle</div>
                        <div className="program-level-title">{selectedMacrocycle.name}</div>
                      </div>
                      <div className="program-level-duration">
                        {formatDateRange(selectedMacrocycle.startDate, selectedMacrocycle.endDate)}
                      </div>
                    </div>
                    {enrichedSelectedMacro?.focus && (
                      <div className="program-level-focus">{enrichedSelectedMacro.focus}</div>
                    )}

                    {/* Mesocycle Row */}
                    {mesosForSelectedMacro.length > 0 && (
                    <div className="program-meso-box">
                      <div className="program-level-header">
                        <div className="program-level-header-left">
                          <div className="program-level-badge meso">Mesocycles</div>
                          <div className="program-level-title">Training Phases</div>
                        </div>
                        <div className="program-level-duration">
                          {mesosForSelectedMacro[0]?.totalWeeks || 4} weeks each
                        </div>
                      </div>
                      
                      <div className="program-meso-blocks">
                        {mesosForSelectedMacro.map((meso, idx) => {
                          const mesoId = getMesoId(meso);
                          const isCurrent = isViewingCurrent && mesoId === activeMesoId;
                          const isHovered = hoveredPhase === mesoId;
                          return (
                            <div 
                              key={mesoId || idx} 
                              className={`program-meso-block ${isCurrent ? 'current' : ''} ${isHovered ? 'hovered' : ''}`}
                              onMouseEnter={() => setHoveredPhase(mesoId)}
                              onMouseLeave={() => setHoveredPhase(null)}
                              onClick={() => setHoveredPhase(isHovered ? null : mesoId)}
                            >
                              <div className="program-meso-block-phase">
                                {meso.strengthPhase ? capitalize(meso.strengthPhase) : `Phase ${(meso.order ?? idx + 1)}`}
                              </div>
                              <div className="program-meso-block-name">
                                {meso.name || capitalize(meso.strengthPhase) || `Mesocycle ${idx + 1}`}
                              </div>
                              {isCurrent && <div className="program-meso-block-current">Current</div>}
                              {isHovered && (
                                <div className="program-meso-block-details">
                                  {meso.phaseDescription && (
                                    <div className="program-meso-block-focus">{meso.phaseDescription}</div>
                                  )}
                                  {meso.repRanges && (
                                    <div className="program-meso-block-reps">{meso.repRanges}</div>
                                  )}
                                  {meso.intensity && (
                                    <div className="program-meso-block-reps">Intensity: {capitalize(meso.intensity)}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="program-meso-note">
                        {hoveredPhase ? (
                          <>Viewing: {mesosForSelectedMacro.find(m => getMesoId(m) === hoveredPhase)?.name || 'Phase details'}</>
                        ) : isViewingCurrent && activeMesocycle ? (
                          <>Tap a phase to see details — you're currently in {activeMesocycle.name || 'this phase'}</>
                        ) : (
                          <>Tap a phase to see details</>
                        )}
                      </div>

                      {/* Microcycle Row — weeks within mesocycle */}
                      <div className="program-micro-box">
                        <div className="program-level-header">
                          <div className="program-level-header-left">
                            <div className="program-level-badge micro">Microcycles</div>
                            <div className="program-level-title">Weekly Schedule</div>
                          </div>
                          <div className="program-level-duration">1 week each</div>
                        </div>

                        <div className="program-week-blocks">
                          {Array.from({ length: selectedMesoTotalWeeks }, (_, i) => i + 1).map((week) => {
                            const isCurrentWeek = isViewingCurrent && week === currentWeek;
                            return (
                              <div 
                                key={week} 
                                className={`program-week-block ${isCurrentWeek ? 'current' : ''} ${hoveredWeek === week ? 'hovered' : ''}`}
                                onMouseEnter={() => setHoveredWeek(week)}
                                onMouseLeave={() => setHoveredWeek(null)}
                                onClick={() => setHoveredWeek(hoveredWeek === week ? null : week)}
                              >
                                <div className="program-week-label">Week</div>
                                <div className="program-week-number">{week}</div>
                                {isCurrentWeek && <div className="program-week-marker">You are here</div>}
                              </div>
                            );
                          })}
                        </div>

                        <div className="program-micro-note">
                          {hoveredWeek ? (
                            <>Week {hoveredWeek} of {selectedMesoTotalWeeks} in this mesocycle</>
                          ) : (
                            <>Tap a week to select — each microcycle is 1 week of training</>
                          )}
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
                )}

                {/* Mesocycle Phases Table */}
                {mesosForSelectedMacro.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h3 className="program-section-title">Training Phases</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="program-phases-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th className="hide-mobile">Focus</th>
                          <th>Weeks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mesosForSelectedMacro.map((meso, idx) => (
                          <tr key={getMesoId(meso) || idx}>
                            <td className="phase-number">{meso.order ?? idx + 1}</td>
                            <td className="phase-name">{meso.name || capitalize(meso.strengthPhase) || `Phase ${idx + 1}`}</td>
                            <td className="phase-focus hide-mobile">{meso.phaseDescription || capitalize(meso.strengthPhase) || '—'}</td>
                            <td className="phase-reps">{meso.totalWeeks || '—'} wks{meso.repRanges ? ` · ${meso.repRanges}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}
              </div>

              {/* Selected Macrocycle Detail Card */}
              {selectedMacrocycle && (
                <div className="dashboard-card settings-section">
                  <div className="program-current-header">
                    <div>
                      <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                        {isViewingCurrent ? 'Current Macrocycle' : 'Macrocycle Details'}
                      </div>
                      <h2 className="settings-section-title" style={{ marginBottom: '4px' }}>
                        {selectedMacrocycle.name}
                      </h2>
                      {enrichedSelectedMacro?.phase && (
                        <div style={{ color: '#FF0000', fontSize: '14px', fontWeight: '500' }}>
                          {capitalize(enrichedSelectedMacro.phase)}
                        </div>
                      )}
                    </div>
                    {enrichedSelectedMacro?.phase && (
                    <div className="program-phase-badge">
                      <div className="program-phase-label">Phase</div>
                      <div className="program-phase-number" style={{ fontSize: '14px' }}>{capitalize(enrichedSelectedMacro.phase)}</div>
                    </div>
                    )}
                  </div>

                  <div style={{ marginTop: '16px', color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)', fontSize: '14px' }}>
                    <strong style={{ color: isDark ? '#FFF' : '#1A1A1A' }}>Duration:</strong> {formatDateRange(selectedMacrocycle.startDate, selectedMacrocycle.endDate)}
                  </div>
                  {enrichedSelectedMacro?.focus && (
                    <div style={{ marginTop: '8px', color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)', fontSize: '14px' }}>
                      <strong style={{ color: isDark ? '#FFF' : '#1A1A1A' }}>Focus:</strong> {enrichedSelectedMacro.focus}
                    </div>
                  )}

                  {/* Goals */}
                  {programGoals.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <h3 className="program-section-title">Goals</h3>
                      <ul className="program-goals-list">
                        {programGoals.map((goal, i) => (
                          <li key={i}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Weekly Schedule — full week, cards stacked under each day */}
                  {weeklySchedule && weeklySchedule.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 className="program-section-title">Weekly Schedule</h3>
                      <div className="program-week-grid">
                        {weeklySchedule.map((entry) => {
                          const isFromProgram = entry.type === 'strength' || entry.type === 'strength_workout';
                          const isRest = entry.type === 'rest';
                          const isToday = isViewingCurrent && entry.dayKey === todayDayKey;
                          return (
                            <div key={entry.dayKey} className="program-week-column">
                              <div className={`program-week-column-header ${isToday ? 'today' : ''}`}>
                                {entry.dayLabel}
                              </div>
                              <div className={`program-week-column-card ${isFromProgram ? 'program' : ''} ${isRest ? 'rest' : ''}`}>
                                <div className="program-week-column-card-name">{entry.activity}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="settings-modal-overlay" onClick={() => !importing && setShowImportModal(false)}>
          <div className="settings-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="settings-modal-header">
              <h2 className="settings-modal-title">Import Workout Program</h2>
              <button
                className="settings-modal-close"
                onClick={() => !importing && setShowImportModal(false)}
                disabled={importing}
              >
                ×
              </button>
            </div>
            <div className="settings-modal-body">
              {importSuccess && (
                <div style={{
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.3)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  color: '#00FF88',
                  textAlign: 'left'
                }}>
                  {importSuccess}
                </div>
              )}

              {importError && (
                <div style={{
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 0, 0, 0.3)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  color: '#FF0000',
                  textAlign: 'left'
                }}>
                  {importError}
                </div>
              )}

              <p style={{ 
                color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)', 
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Select a program to import. This will replace any existing scheduled workouts.
              </p>

              {user?.personaId === 'multi_sport_training' && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: isDark ? '#FFF' : '#1A1A1A', fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'left' }}>
                    Multi-Sport Program
                  </h3>
                  
                  <button
                    onClick={() => handleImportWorkouts('multi_sport_auto')}
                    disabled={importing}
                    style={{
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '12px',
                      padding: '20px',
                      textAlign: 'left',
                      cursor: importing ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: importing ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    <div style={{ color: isDark ? '#FFF' : '#1A1A1A', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Reset & Re-import Multi-Sport Program
                    </div>
                    <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', fontSize: '14px', lineHeight: '1.5' }}>
                      Clears all scheduled workouts and re-imports based on today's date.
                    </div>
                  </button>
                </div>
              )}

              {importing && (
                <div style={{ textAlign: 'left', padding: '20px', color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }}>
                  Importing workouts...
                </div>
              )}
            </div>
            <div className="settings-modal-footer">
              <button
                className="settings-modal-cancel"
                onClick={() => setShowImportModal(false)}
                disabled={importing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    <BottomNav userId={userId} user={user} />
    </>
  );
}

export default Program;
